import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { Box, TablePagination, variables } from '@hopskipdrive/react-components';
import * as Constants from '@rideiq/constants';
import { Container } from '@rideiq/shared/components/TablePaginationContainer/TablePaginationContainer.styles';

import { useInnerDimensions } from './useInnerDimensions';

const PAGINATION_BAR_HEIGHT = 53;

interface TablePaginationContainerProps {
  totalCount: number;
  handlePageChange: (params: QueryParams, isPaginated: boolean) => void;
  children: ReactNode;
  filterKey?: string;
  isFetching: boolean;
  heightOffset?: number;
  initialPerPage?: number;
  alwaysConstrainHeight?: boolean;
}

export const TablePaginationContainer: FC<TablePaginationContainerProps> = ({
  totalCount,
  handlePageChange,
  children,
  filterKey,
  isFetching,
  heightOffset,
  initialPerPage = Constants.DEFAULT_ROWS_PER_PAGE,
  alwaysConstrainHeight = false,
}) => {
  const { dimensions } = useInnerDimensions();
  const tableContainerOffset = heightOffset ?? variables.headingHeights.withTitleTabs + 95;
  const tableHeightOffset = tableContainerOffset + PAGINATION_BAR_HEIGHT;
  const [pageParams, setPageParams] = useState<QueryParams>({
    page: 1,
    perPage: initialPerPage,
  });
  const [rowsPerPage, setRowsPerPage] = useState(initialPerPage);
  const skipPageChangeRef = useRef(false);
  const prevFilterKeyRef = useRef(filterKey);
  const showPaginationRef = useRef(false);

  if (!isFetching) {
    showPaginationRef.current = totalCount > initialPerPage;
  }
  const showPagination = showPaginationRef.current;
  const shouldConstrainHeight = alwaysConstrainHeight || showPagination;

  const handlePagination = (pageNumber: number) => {
    setPageParams({
      perPage: rowsPerPage,
      page: pageNumber,
    });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);

    setPageParams({ page: 1, perPage: value });
    setRowsPerPage(value);
  };

  useEffect(() => {
    if (skipPageChangeRef.current) {
      skipPageChangeRef.current = false;

      return;
    }
    handlePageChange(pageParams, true);
  }, [pageParams]);

  useEffect(() => {
    if (prevFilterKeyRef.current === filterKey) {
      return;
    }
    prevFilterKeyRef.current = filterKey;
    skipPageChangeRef.current = true;
    setPageParams({ page: 1, perPage: initialPerPage });
    setRowsPerPage(initialPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  return (
    <Box
      data-testid="table-pagination-scroll-container"
      sx={
        shouldConstrainHeight
          ? { maxHeight: `${dimensions.height - tableContainerOffset}px`, overflow: 'auto' }
          : undefined
      }
    >
      <DataTableContainer innerDimensions={dimensions} heightOffset={tableHeightOffset} minWidth="1158px">
        {children}
      </DataTableContainer>
      {showPagination && (
        <TablePagination
          component="div"
          data-testid="pagination-section"
          count={totalCount}
          page={pageParams.page - 1}
          onPageChange={(_, page) => handlePagination(page + 1)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            position: 'sticky',
            bottom: 0,
            left: 0,
            background: '#fff',
            width: '100%',
          }}
        />
      )}
    </Box>
  );
};

type DataTableContainerProps = {
  innerDimensions: {
    height: number;
    width: number;
  };
  heightOffset: number;
  minWidth: string;
  children: ReactNode | string;
};

const DataTableContainer: React.FC<DataTableContainerProps> = ({
  children,
  innerDimensions,
  heightOffset,
  minWidth,
}) => {
  return (
    <Container minHeight={`${innerDimensions.height - (heightOffset || 0)}px`} minWidth={minWidth}>
      {children}
    </Container>
  );
};
